<template>
  <div class="screen">
    <div class="map-shell" :style="mapShellStyle">
      <div ref="map" class="map-canvas"></div>

      <div class="map-title" aria-label="大理市定点医药机构分布图">
        <span
          v-for="(char, index) in mapTitleChars"
          :key="`${char}-${index}`"
          class="map-title__char"
        >
          {{ char }}
        </span>
      </div>

      <form class="map-search" @submit.prevent="applySearch">
        <input
          v-model.trim="searchKeyword"
          class="map-search__input"
          type="text"
          :placeholder="`搜索${activeSourceMeta.label}名称`"
        >
        <button class="map-search__button" type="submit">
          搜索
        </button>
      </form>

      <section class="legend-card">
        <div class="legend-tabs">
          <button
            v-for="item in sourceOptions"
            :key="item.key"
            class="legend-tab"
            :class="{ active: activeSource === item.key }"
            @click="switchSource(item.key)"
          >
            {{ item.label }}
          </button>
        </div>

        <div class="legend-summary">
          <p class="legend-value">{{ totalCount }}</p>
          <p class="legend-label">{{ summaryLabel }}</p>
        </div>

        <div class="legend-extra legend-extra--summary">
          <button
            type="button"
            class="legend-metric"
            :class="{ active: activeQuickFilter === 'institutionRescue' }"
            :aria-pressed="activeQuickFilter === 'institutionRescue' ? 'true' : 'false'"
            @click="toggleQuickFilter('institutionRescue')"
          >
            <p class="legend-metric__value">{{ institutionRescueTotal }}</p>
            <p class="legend-metric__label">医疗救助机构</p>
          </button>
          <button
            type="button"
            class="legend-metric"
            :class="{ active: activeQuickFilter === 'slowDisease' }"
            :aria-pressed="activeQuickFilter === 'slowDisease' ? 'true' : 'false'"
            @click="toggleQuickFilter('slowDisease')"
          >
            <p class="legend-metric__value">{{ pharmacyScopeStats.slowDisease }}</p>
            <p class="legend-metric__label">特慢病</p>
          </button>
          <button
            type="button"
            class="legend-metric"
            :class="{ active: activeQuickFilter === 'dualChannel' }"
            :aria-pressed="activeQuickFilter === 'dualChannel' ? 'true' : 'false'"
            @click="toggleQuickFilter('dualChannel')"
          >
            <p class="legend-metric__value">{{ pharmacyScopeStats.dualChannel }}</p>
            <p class="legend-metric__label">双通道</p>
          </button>
        </div>
      </section>

      <div
        v-for="card in popupCards"
        :key="card.id"
        class="point-popup"
        :style="{
          left: `${card.screen.x}px`,
          top: `${card.screen.y}px`
        }"
      >
        <button class="point-popup__close" @click="closePopup(card.id)">×</button>
        <p class="point-popup__title">名称：{{ card.name }}</p>
        <p v-if="card.source === 'institutions'">机构等级：{{ card.level || '-' }}</p>
        <p>联系电话：{{ card.phone || '-' }}</p>
        <p>地址：{{ card.address || '-' }}</p>
        <p>{{ card.extraLabel || '类型' }}：{{ card.extraValue || card.subtitle || '-' }}</p>
      </div>

      <router-link class="return-home" :to="{ name: 'home' }" aria-label="返回首页">
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <path
            d="M90 28c-17 3-31 12-40 25H28l22 22 22-22H56c7-9 18-15 31-17 6-1 9-1 13-1v-7c-4-1-7-1-10 0z"
            fill="currentColor"
          />
        </svg>
      </router-link>

      <div v-if="loading" class="notice-card">
        正在加载{{ activeSourceMeta.label }}点位...
      </div>

      <div v-if="errorMessage" class="notice-card error">
        <p>{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { hasAmapKey, loadAmap, loadAmapPlugins } from '../utils/amap'

const SOURCE_META = {
  institutions: {
    key: 'institutions',
    label: '医疗机构',
    shortLabel: '机构',
    endpoint: '/api/admin/institutions'
  },
  pharmacies: {
    key: 'pharmacies',
    label: '零售药店',
    shortLabel: '零售药店',
    endpoint: '/api/admin/pharmacies'
  }
}

const DEFAULT_MARKER_COLOR = '#ef4444'
const DEFAULT_PROJECT_CENTER = [100.236, 25.767]
const DEFAULT_INITIAL_ZOOM = 11.82
const DEFAULT_FLAG_BLINK_SECONDS = 10
const DEFAULT_IDLE_RESET_SECONDS = 30
const MARKER_HEIGHT_RATIO = 60 / 52
const DEFAULT_MARKER_BASE_WIDTH = 18
const DEFAULT_MARKER_MIN_WIDTH = 14
const DEFAULT_MARKER_MAX_WIDTH = 72
const DEFAULT_MARKER_ZOOM_WIDTH_STEP = 8
const INLINE_FLAG_SVG = `
  <svg class="marker-flag__svg" viewBox="0 0 1024 1024" aria-hidden="true" focusable="false">
    <path d="M709.05 229.212c67.984 0 112.808-6.9 154.512-31.553v389.839c-41.748 24.61-85.166 41.704-153.149 41.704-67.895 0-93.076-7.427-134.648-32.212-41.529-24.521-86.573-32.168-154.468-32.168-67.896 0-125.464 24.038-167.124 48.78V209.305c41.66-24.83 99.228-48.867 167.124-48.867s112.94 7.69 154.468 32.211c41.616 24.522 65.478 36.563 133.286 36.563z m-525.146 634.35a24.038 24.038 0 0 1-16.611-6.591 21.797 21.797 0 0 1-6.855-15.733V182.762c0-12.305 10.502-22.325 23.422-22.324 12.964 0 23.467 9.975 23.467 22.324v658.476a21.797 21.797 0 0 1-6.855 15.82 24.038 24.038 0 0 1-16.612 6.504z" fill="currentColor"/>
  </svg>
`
const QUICK_FILTER_META = {
  institutionRescue: {
    key: 'institutionRescue',
    source: 'institutions',
    params: {
      type: '医疗救助机构'
    }
  },
  slowDisease: {
    key: 'slowDisease',
    source: 'pharmacies',
    params: {
      scope: '特慢病'
    }
  },
  dualChannel: {
    key: 'dualChannel',
    source: 'pharmacies',
    params: {
      scope: '双通道'
    }
  }
}

export default {
  name: 'IndexView',
  data() {
    return {
      loading: true,
      errorMessage: '',
      map: null,
      districtSearch: null,
      districtPolygons: [],
      markerEntries: [],
      points: [],
      totalCount: 0,
      institutionRescueTotal: 0,
      pharmacyScopeTotals: {
        slowDisease: 0,
        dualChannel: 0
      },
      popupCards: [],
      searchKeyword: '',
      activeSource: this.normalizeSource(this.$route.query.source),
      activeQuickFilter: '',
      idleTimer: null,
      activityHandler: null,
      popupRefreshFrame: null,
      cachedMarkerVisualKey: '',
      lastActivityAt: Date.now(),
      projectCenter: DEFAULT_PROJECT_CENTER,
      initialZoom: DEFAULT_INITIAL_ZOOM,
      flagBlinkSeconds: DEFAULT_FLAG_BLINK_SECONDS,
      idleResetSeconds: DEFAULT_IDLE_RESET_SECONDS
    }
  },
  computed: {
    sourceOptions() {
      return Object.values(SOURCE_META)
    },
    mapTitleChars() {
      return '大理市定点医药机构分布图'.split('')
    },
    activeSourceMeta() {
      return SOURCE_META[this.activeSource]
    },
    mapShellStyle() {
      return {
        '--flag-blink-seconds': `${this.flagBlinkSeconds}s`
      }
    },
    idleResetMs() {
      return this.idleResetSeconds * 1000
    },
    pharmacyScopeStats() {
      return this.pharmacyScopeTotals
    },
    activeQuickFilterMeta() {
      return QUICK_FILTER_META[this.activeQuickFilter] || null
    },
    summaryLabel() {
      return this.activeQuickFilterMeta ? '当前筛选总数' : '当前总数'
    }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.clearMarkers()
    this.clearIdleTimer()
    this.cancelPopupRefresh()
    this.removeActivityListeners()

    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },
  methods: {
    normalizeSource(source) {
      return SOURCE_META[source] ? source : 'institutions'
    },
    async initMap() {
      if (!hasAmapKey()) {
        this.loading = false
        this.errorMessage = '没有检测到高德地图 Key。'
        return
      }

      try {
        await this.fetchRuntimeSettings()
        await this.fetchSummaryTotals()
        const AMap = await loadAmap()

        this.map = new AMap.Map(this.$refs.map, {
          center: this.projectCenter,
          zoom: this.initialZoom,
          resizeEnable: true,
          viewMode: '2D'
        })

        await this.loadDistrictBoundary()
        this.resetMapView()
        this.bindMapEvents()
        this.registerActivityListeners()
        await this.fetchPoints()
        this.scheduleIdleReset()
      } catch (error) {
        this.errorMessage = this.getReadableError(error)
      } finally {
        this.loading = false
      }
    },
    async fetchRuntimeSettings() {
      try {
        const { data } = await axios.get('/api/settings/map')
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '地图配置加载失败')
        }

        const payload = data.data || {}
        const blinkSeconds = Number(payload.flag_blink_seconds)
        const idleResetSeconds = Number(payload.idle_reset_seconds)

        this.flagBlinkSeconds = blinkSeconds > 0 ? blinkSeconds : DEFAULT_FLAG_BLINK_SECONDS
        this.idleResetSeconds = idleResetSeconds > 0 ? idleResetSeconds : DEFAULT_IDLE_RESET_SECONDS
      } catch (error) {
        this.flagBlinkSeconds = DEFAULT_FLAG_BLINK_SECONDS
        this.idleResetSeconds = DEFAULT_IDLE_RESET_SECONDS
      }
    },
    async fetchSummaryTotals() {
      const [institutionResult, pharmacyResult] = await Promise.allSettled([
        axios.get(SOURCE_META.institutions.endpoint, {
          params: {
            page: 1,
            page_size: 1,
            type: '医疗救助机构'
          }
        }),
        axios.get(SOURCE_META.pharmacies.endpoint, {
          params: {
            page: 1,
            page_size: 9999
          }
        })
      ])

      if (institutionResult.status === 'fulfilled') {
        const { data } = institutionResult.value
        this.institutionRescueTotal = data?.code === 0
          ? Number(data?.data?.total || 0)
          : 0
      } else {
        this.institutionRescueTotal = 0
      }

      if (pharmacyResult.status === 'fulfilled') {
        const { data } = pharmacyResult.value
        const list = Array.isArray(data?.data?.list) ? data.data.list : []
        this.pharmacyScopeTotals = list.reduce((stats, item) => {
          const scope = String(item.medical_scope || '').replace(/\s+/g, '')
          if (scope.includes('特慢病')) {
            stats.slowDisease += 1
          }
          if (scope.includes('双通道')) {
            stats.dualChannel += 1
          }
          return stats
        }, {
          slowDisease: 0,
          dualChannel: 0
        })
      } else {
        this.pharmacyScopeTotals = {
          slowDisease: 0,
          dualChannel: 0
        }
      }
    },
    async fetchPoints() {
      this.loading = true
      this.errorMessage = ''

      try {
        const quickFilterParams = this.activeQuickFilterMeta && this.activeQuickFilterMeta.source === this.activeSource
          ? this.activeQuickFilterMeta.params
          : {}
        const { data } = await axios.get(this.activeSourceMeta.endpoint, {
          params: {
            page: 1,
            page_size: 9999,
            ...(this.searchKeyword ? { keyword: this.searchKeyword } : {}),
            ...quickFilterParams
          }
        })

        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '接口请求失败')
        }

        const payload = data.data || {}
        const list = Array.isArray(payload.list) ? payload.list : []

        this.points = list
          .map(item => this.mapRecord(item, this.activeSource))
          .filter(item => item && Array.isArray(item.position))
        this.totalCount = Number(payload.total) || this.points.length

        this.renderMarkers()
        this.popupCards = []
        this.focusResults()
      } catch (error) {
        this.points = []
        this.totalCount = 0
        this.clearMarkers()
        this.popupCards = []
        this.errorMessage = error.response?.data?.message || error.message || '点位加载失败'
      } finally {
        this.loading = false
      }
    },
    mapRecord(item, source) {
      const longitude = Number(item.longitude)
      const latitude = Number(item.latitude)
      if (Number.isNaN(longitude) || Number.isNaN(latitude)) {
        return null
      }

      if (source === 'institutions') {
        return {
          id: `${source}-${item.id}`,
          source,
          name: item.standard_name,
          level: item.institution_level || '-',
          subtitle: item.institution_type || '定点医疗机构',
          extraLabel: '机构类型',
          extraValue: item.institution_type || '定点医疗机构',
          phone: item.contact_phone,
          address: item.address,
          position: [longitude, latitude],
          markerColor: DEFAULT_MARKER_COLOR
        }
      }

      return {
        id: `${source}-${item.id}`,
        source,
        name: item.standard_name,
        subtitle: item.status || '定点药店',
        extraLabel: '医疗开通范围',
        extraValue: item.medical_scope || '定点药店购药',
        phone: item.contact_phone,
        address: item.address,
        position: [longitude, latitude],
        markerColor: DEFAULT_MARKER_COLOR
      }
    },
    renderMarkers() {
      if (!this.map || !window.AMap) {
        return
      }

      this.clearMarkers()
      const visual = this.getMarkerVisual()
      this.cachedMarkerVisualKey = visual.key

      this.markerEntries = this.points.map(point => {
        const marker = new window.AMap.Marker({
          position: point.position,
          anchor: 'bottom-left',
          content: this.createMarkerHtml(visual),
          offset: this.getMarkerOffset(visual)
        })

        marker.on('click', () => {
          this.recordActivity()
          this.openPopup(point, marker)
        })

        return { point, marker }
      })

      if (this.markerEntries.length) {
        this.map.add(this.markerEntries.map(item => item.marker))
        this.refreshPopupPositions()
      }
    },
    clearMarkers() {
      if (!this.map || !this.markerEntries.length) {
        this.markerEntries = []
        return
      }

      this.map.remove(this.markerEntries.map(item => item.marker))
      this.markerEntries = []
    },
    async loadDistrictBoundary() {
      await loadAmapPlugins(['AMap.DistrictSearch'])

      if (typeof window.AMap?.DistrictSearch !== 'function') {
        throw new Error('DistrictSearch 插件不可用')
      }

      this.districtSearch = new window.AMap.DistrictSearch({
        level: 'district',
        subdistrict: 0,
        extensions: 'all',
        showbiz: false
      })

      const district = await new Promise((resolve, reject) => {
        this.districtSearch.search('大理市', (status, result) => {
          if (status !== 'complete' || !result || !result.districtList || !result.districtList.length) {
            reject(new Error('大理市行政边界查询失败'))
            return
          }

          resolve(result.districtList[0])
        })
      })

      const boundaries = district.boundaries || []

      this.districtPolygons.forEach(polygon => this.map.remove(polygon))
      this.districtPolygons = boundaries.map(boundary => {
        const polygon = new window.AMap.Polygon({
          path: boundary,
          strokeColor: '#2f81ff',
          strokeWeight: 3,
          strokeOpacity: 1,
          fillOpacity: 0,
          bubble: true,
          zIndex: 90
        })

        this.map.add(polygon)
        return polygon
      })
    },
    bindMapEvents() {
      if (!this.map || this.__mapEventsBound) {
        return
      }

      this.map.on('dragstart', () => this.recordActivity())
      this.map.on('zoomstart', () => this.recordActivity())
      this.map.on('mapmove', () => this.schedulePopupRefresh())
      this.map.on('moveend', () => this.refreshPopupPositions())
      this.map.on('zoomchange', () => this.schedulePopupRefresh())
      this.map.on('zoomend', () => {
        this.refreshMarkerVisuals()
        this.refreshPopupPositions()
      })
      this.map.on('resize', () => {
        this.refreshMarkerVisuals()
        this.refreshPopupPositions()
      })
      this.map.on('click', () => this.recordActivity())
      this.__mapEventsBound = true
    },
    registerActivityListeners() {
      if (this.activityHandler) {
        return
      }

      this.activityHandler = () => this.recordActivity()
      ;['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'].forEach(eventName => {
        window.addEventListener(eventName, this.activityHandler, { passive: true })
      })
    },
    removeActivityListeners() {
      if (!this.activityHandler) {
        return
      }

      ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel'].forEach(eventName => {
        window.removeEventListener(eventName, this.activityHandler)
      })

      this.activityHandler = null
    },
    recordActivity() {
      this.lastActivityAt = Date.now()

      if (!this.idleTimer) {
        this.scheduleIdleReset()
      }
    },
    scheduleIdleReset() {
      this.clearIdleTimer()
      this.idleTimer = window.setTimeout(() => {
        this.handleIdleTimeout()
      }, this.idleResetMs)
    },
    handleIdleTimeout() {
      this.idleTimer = null
      const inactiveFor = Date.now() - this.lastActivityAt

      if (inactiveFor < this.idleResetMs) {
        this.idleTimer = window.setTimeout(() => {
          this.handleIdleTimeout()
        }, this.idleResetMs - inactiveFor)
        return
      }

      this.popupCards = []
      if (this.searchKeyword) {
        this.searchKeyword = ''
        this.activeQuickFilter = ''
        this.fetchPoints()
        return
      }

      if (this.activeQuickFilter) {
        this.activeQuickFilter = ''
        this.fetchPoints()
        return
      }

      this.resetMapView()
    },
    clearIdleTimer() {
      if (this.idleTimer) {
        window.clearTimeout(this.idleTimer)
        this.idleTimer = null
      }
    },
    getSearchFitPadding() {
      const width = window.innerWidth || 1920

      if (width >= 3200) {
        return [220, 320, 280, 320]
      }

      if (width >= 2560) {
        return [200, 280, 240, 280]
      }

      if (width >= 1440) {
        return [170, 220, 210, 220]
      }

      return [120, 140, 180, 140]
    },
    resetMapView() {
      if (!this.map) {
        return
      }

      this.map.setZoomAndCenter(this.initialZoom, this.projectCenter)
    },
    focusResults() {
      if (!this.map) {
        return
      }

      if (!this.searchKeyword && !this.activeQuickFilter) {
        this.resetMapView()
        return
      }

      if (this.markerEntries.length === 1) {
        this.map.setZoomAndCenter(Math.max(this.initialZoom, 16.4), this.markerEntries[0].point.position)
        return
      }

      if (this.markerEntries.length > 1) {
        this.map.setFitView(
          this.markerEntries.map(item => item.marker),
          false,
          this.getSearchFitPadding()
        )
        return
      }

      this.resetMapView()
    },
    applySearch() {
      this.recordActivity()
      this.popupCards = []
      this.fetchPoints()
    },
    switchSource(source) {
      if (this.activeSource === source && !this.activeQuickFilter) {
        return
      }

      this.recordActivity()
      this.activeSource = source
      this.activeQuickFilter = ''
      this.popupCards = []
      this.syncRouteSource(source)
      this.fetchPoints()
    },
    toggleQuickFilter(filterKey) {
      const nextFilterKey = this.activeQuickFilter === filterKey ? '' : filterKey
      const nextFilter = QUICK_FILTER_META[nextFilterKey] || null
      const nextSource = nextFilter ? nextFilter.source : this.activeSource

      this.recordActivity()
      this.searchKeyword = ''
      this.activeQuickFilter = nextFilterKey
      this.popupCards = []

      if (this.activeSource !== nextSource) {
        this.activeSource = nextSource
        this.syncRouteSource(nextSource)
      }

      this.fetchPoints()
    },
    syncRouteSource(source) {
      this.$router.replace({
        name: 'map',
        query: {
          source
        }
      })
    },
    openPopup(point) {
      const existingIndex = this.popupCards.findIndex(item => item.id === point.id)
      const card = {
        ...point,
        screen: this.getPopupScreenPosition(point.position)
      }

      if (existingIndex >= 0) {
        this.$set(this.popupCards, existingIndex, card)
      } else {
        this.popupCards.push(card)
      }
    },
    closePopup(id) {
      this.popupCards = this.popupCards.filter(item => item.id !== id)
      this.recordActivity()
    },
    schedulePopupRefresh() {
      if (!this.map || !this.popupCards.length || this.popupRefreshFrame) {
        return
      }

      this.popupRefreshFrame = window.requestAnimationFrame(() => {
        this.popupRefreshFrame = null
        this.refreshPopupPositions()
      })
    },
    cancelPopupRefresh() {
      if (!this.popupRefreshFrame) {
        return
      }

      window.cancelAnimationFrame(this.popupRefreshFrame)
      this.popupRefreshFrame = null
    },
    getPopupScreenPosition(position) {
      if (!this.map) {
        return { x: 0, y: 0 }
      }

      const pixel = this.map.lngLatToContainer(position)
      return {
        x: pixel.getX() + 16,
        y: pixel.getY() - 118
      }
    },
    refreshPopupPositions() {
      if (!this.map || !this.popupCards.length) {
        return
      }

      this.popupCards = this.popupCards.map(card => ({
        ...card,
        screen: this.getPopupScreenPosition(card.position)
      }))
    },
    getMarkerVisual() {
      const zoom = this.map ? this.map.getZoom() : this.initialZoom
      const minWidth = Math.min(DEFAULT_MARKER_MIN_WIDTH, DEFAULT_MARKER_BASE_WIDTH)
      const maxWidth = Math.max(DEFAULT_MARKER_MAX_WIDTH, DEFAULT_MARKER_BASE_WIDTH)
      const width = Math.min(
        maxWidth,
        Math.max(
          minWidth,
          DEFAULT_MARKER_BASE_WIDTH + (zoom - this.initialZoom) * DEFAULT_MARKER_ZOOM_WIDTH_STEP
        )
      )
      const height = width * MARKER_HEIGHT_RATIO

      return {
        key: `${Math.round(width * 10)}-${Math.round(height * 10)}`,
        width: Number(width.toFixed(2)),
        height: Number(height.toFixed(2)),
        offsetX: Math.round(-0.12 * width),
        offsetY: Math.round(-0.38 * height)
      }
    },
    createMarkerHtml(visual = this.getMarkerVisual()) {
      return `
        <div class="marker-flag" style="width:${visual.width}px;height:${visual.height}px;">
          ${INLINE_FLAG_SVG}
        </div>
      `
    },
    getMarkerOffset(visual = this.getMarkerVisual()) {
      return new window.AMap.Pixel(visual.offsetX, visual.offsetY)
    },
    refreshMarkerVisuals() {
      if (!this.map || !this.markerEntries.length || !window.AMap) {
        return
      }

      const visual = this.getMarkerVisual()
      if (this.cachedMarkerVisualKey === visual.key) {
        return
      }

      this.cachedMarkerVisualKey = visual.key
      const content = this.createMarkerHtml(visual)
      const offset = this.getMarkerOffset(visual)

      this.markerEntries.forEach(({ marker }) => {
        marker.setContent(content)
        marker.setOffset(offset)
      })
    },
    getReadableError(error) {
      if (error && error.message === 'missing-key') {
        return '没有检测到高德地图 Key。'
      }

      if (error && error.message) {
        return `高德地图初始化失败：${error.message}`
      }

      return '高德地图初始化失败，请检查 Key、域名白名单和安全密钥配置。'
    }
  }
}
</script>

<style>
.screen,
.map-shell,
.map-canvas {
  width: 100%;
  height: 100%;
}

.map-shell {
  position: relative;
  overflow: hidden;
}

.map-title,
.map-search,
.legend-card,
.notice-card,
.point-popup,
.return-home {
  position: absolute;
  z-index: 2;
}

.legend-card,
.notice-card,
.point-popup {
  border: 1px solid rgba(255, 255, 255, 0.78);
  background: rgba(252, 254, 255, 0.94);
  box-shadow: 0 24px 60px rgba(16, 38, 63, 0.12);
  backdrop-filter: blur(14px);
}

.map-title {
  top: clamp(4px, 0.2vh, 12px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(16px, 0.92vw, 26px);
  z-index: 2;
  width: min(1640px, calc(100% - 900px));
  padding: 0 clamp(72px, 4vw, 160px);
  box-sizing: border-box;
  text-align: center;
  white-space: nowrap;
  color: #0f4fb8;
  font-size: clamp(54px, 2.6vw, 102px);
  font-weight: 900;
  line-height: 1.08;
  text-shadow: 0 10px 28px rgba(15, 79, 184, 0.18);
}

.map-title__char {
  display: inline-block;
  flex: 0 0 auto;
}

.map-search {
  top: clamp(10px, 1vh, 24px);
  right: clamp(26px, 1.7vw, 58px);
  z-index: 2;
  display: flex;
  align-items: center;
  gap: clamp(12px, 0.75vw, 22px);
  padding: clamp(12px, 0.9vh, 22px) clamp(12px, 0.8vw, 22px);
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  background: rgba(252, 254, 255, 0.94);
  box-shadow: 0 24px 60px rgba(16, 38, 63, 0.12);
  backdrop-filter: blur(14px);
}

.map-search__input {
  width: clamp(340px, 16vw, 620px);
  height: clamp(48px, 3.4vh, 76px);
  padding: 0 clamp(20px, 1.2vw, 34px);
  border: 0;
  border-radius: 999px;
  background: #f3f7fb;
  color: #10263f;
  font-size: clamp(16px, 0.9vw, 28px);
  outline: none;
}

.map-search__button {
  height: clamp(48px, 3.4vh, 76px);
  padding: 0 clamp(22px, 1.25vw, 40px);
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
  font-size: clamp(16px, 0.9vw, 28px);
  font-weight: 700;
  cursor: pointer;
}

.legend-card {
  left: clamp(26px, 1.8vw, 58px);
  bottom: clamp(26px, 2.2vh, 54px);
  width: clamp(520px, 19vw, 760px);
  padding: clamp(20px, 1.3vw, 34px);
  border-radius: clamp(26px, 1.8vw, 44px);
}

.legend-tabs {
  display: flex;
  gap: clamp(12px, 0.75vw, 22px);
  margin-bottom: clamp(16px, 1.35vh, 30px);
}

.legend-tab {
  min-width: clamp(132px, 5.8vw, 220px);
  height: clamp(46px, 3vh, 66px);
  padding: 0 clamp(18px, 1.05vw, 32px);
  border: 0;
  border-radius: 999px;
  background: #edf1f7;
  color: #48617d;
  font-size: clamp(15px, 0.84vw, 24px);
  font-weight: 700;
  cursor: pointer;
}

.legend-tab.active {
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
}

.legend-summary {
  padding: clamp(20px, 1.4vh, 32px) clamp(18px, 1.15vw, 30px) clamp(16px, 1.1vh, 26px);
  border-radius: clamp(20px, 1.35vw, 34px);
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
}

.legend-extra {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(12px, 0.9vw, 20px);
  margin-top: clamp(14px, 1.2vh, 24px);
}

.legend-extra--summary {
  margin-top: clamp(14px, 1.2vh, 24px);
}

.legend-metric {
  width: 100%;
  padding: clamp(16px, 1.2vh, 26px) clamp(16px, 1.05vw, 24px);
  border: 0;
  border-radius: clamp(18px, 1.2vw, 28px);
  background: linear-gradient(180deg, #edf5ff 0%, #e2efff 100%);
  text-align: left;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.legend-metric:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(47, 129, 255, 0.14);
}

.legend-metric.active {
  background: linear-gradient(135deg, #2f81ff 0%, #69a8ff 100%);
  box-shadow: 0 14px 30px rgba(47, 129, 255, 0.24);
}

.legend-metric.active .legend-metric__value,
.legend-metric.active .legend-metric__label {
  color: #fff;
}

.legend-metric__value {
  margin: 0 0 6px;
  color: #10263f;
  font-size: clamp(30px, 1.65vw, 58px);
  font-weight: 700;
  line-height: 1;
}

.legend-metric__label {
  margin: 0;
  color: #607289;
  font-size: clamp(14px, 0.78vw, 22px);
  font-weight: 700;
  white-space: nowrap;
}

.legend-value {
  margin: 0 0 8px;
  color: #10263f;
  font-size: clamp(56px, 2.6vw, 98px);
  font-weight: 700;
  line-height: 1;
}

.legend-label {
  margin: 0;
  color: #607289;
  font-size: clamp(15px, 0.86vw, 24px);
  font-weight: 700;
}

.point-popup {
  width: min(330px, calc(100vw - 48px));
  padding: 16px 18px 14px;
  border-radius: 18px;
  color: #10263f;
}

.point-popup p {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.6;
}

.point-popup__title {
  padding-right: 26px;
  font-size: 16px !important;
  font-weight: 700;
}

.point-popup__close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8b9cb0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.return-home {
  right: clamp(20px, 1.4vw, 48px);
  bottom: clamp(20px, 1.8vh, 46px);
  width: clamp(164px, 7.2vw, 280px);
  height: clamp(164px, 7.2vw, 280px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2f81ff;
  text-decoration: none;
}

.return-home svg {
  width: 100%;
  height: 100%;
  overflow: visible;
  filter: drop-shadow(0 8px 16px rgba(47, 129, 255, 0.2));
  transform: rotate(90deg) scale(1.55);
  transform-origin: center;
}

.notice-card {
  left: 50%;
  bottom: clamp(28px, 2.4vh, 60px);
  transform: translateX(-50%);
  width: min(clamp(620px, 38vw, 1120px), calc(100% - 48px));
  padding: clamp(18px, 1.2vh, 28px) clamp(20px, 1.2vw, 34px);
  border-radius: clamp(20px, 1.35vw, 34px);
  font-size: clamp(15px, 0.92vw, 26px);
  line-height: 1.7;
  text-align: center;
}

.notice-card.error {
  color: #8f2d2d;
  background: rgba(255, 245, 245, 0.94);
}

.marker-flag {
  position: relative;
  width: calc(52px * var(--marker-scale, 1));
  height: calc(60px * var(--marker-scale, 1));
  overflow: visible;
  color: #dd191d;
}

.marker-flag__svg {
  width: 100%;
  height: 100%;
  display: block;
  user-select: none;
  pointer-events: none;
  color: #d91c1c;
  animation: flag-blink var(--flag-blink-seconds, 10s) ease-in-out infinite;
}

@keyframes flag-blink {
  0%,
  82%,
  100% {
    color: #d91c1c;
    opacity: 0.98;
    transform: scale(1);
  }

  86% {
    color: #f43b3b;
    opacity: 1;
    transform: scale(1.015);
  }

  89% {
    color: #ff6a6a;
    opacity: 1;
    transform: scale(1.03);
  }

  92% {
    color: #ef3131;
    opacity: 1;
    transform: scale(1.01);
  }
}

@media (min-width: 1600px) {
  .map-title {
    width: min(1760px, calc(100% - 980px));
    padding: 0 clamp(84px, 4.8vw, 180px);
    gap: clamp(20px, 1.06vw, 32px);
    font-size: clamp(56px, 2.7vw, 108px);
  }
}

@media (min-width: 3000px) and (min-height: 1600px) {
  .map-title {
    width: min(1800px, calc(100% - 1080px));
    padding: 0 clamp(90px, 5vw, 220px);
    gap: clamp(26px, 1.35vw, 42px);
    font-size: clamp(62px, 2.45vw, 112px);
  }
}

@media (max-width: 960px) {
  .map-title {
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    display: block;
    width: calc(100% - 32px);
    max-width: calc(100% - 32px);
    padding: 0;
    font-size: 34px;
    text-align: center;
    white-space: normal;
    gap: 0;
  }

  .map-title__char {
    display: inline;
  }

  .map-search {
    top: 72px;
    left: 16px;
    right: 16px;
  }

  .map-search__input {
    width: auto;
    min-width: 0;
    flex: 1;
  }

  .legend-card,
  .notice-card {
    width: calc(100% - 32px);
  }

  .legend-card {
    left: 16px;
    bottom: 16px;
  }

  .legend-extra {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .return-home {
    right: 12px;
    bottom: 12px;
    width: 112px;
    height: 112px;
  }
}

@media (max-width: 640px) {
  .map-title {
    font-size: 22px;
  }

  .map-search {
    gap: 8px;
    padding: 8px;
  }

  .map-search__button {
    padding: 0 16px;
  }

  .legend-card {
    width: 240px;
  }

  .legend-tabs {
    flex-direction: column;
  }

  .legend-tab {
    width: 100%;
  }

  .legend-extra {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .point-popup {
    width: min(300px, calc(100vw - 32px));
  }
}
</style>
